import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/pro-light-svg-icons/faDownload';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons/faArrowsRotate';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { faPenToSquare } from '@fortawesome/pro-light-svg-icons/faPenToSquare';
import { TypedMemo } from '../../../common/types';
import { downloadMediaUrl } from '../inlineMediaUrl';
import { trackAssetDownloaded } from '../assetEvents';
import { libraryMessages } from '../library/libraryMessages';
import { AssetShareModel } from './useAssetShare';
import { AssetViewerItem } from './viewerItems';
import { viewerMessages } from './viewerMessages';
import { revisionMessages } from '../revisions/revisionMessages';
import { addedAssetsMessages } from '../addedAssets/addedAssetsMessages';
import { WatermarkDownloadLink } from '../../watermarkUpsell/choice/WatermarkDownloadLink';
import { choiceFileOfViewerItem } from '../../watermarkUpsell/files/choiceFiles';
import type { ViewerRevisionAction } from './viewerRevisionAction';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the viewer; each part uses its own slice of it */
import styles from './AssetViewer.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ViewerActionsProps {
  item: AssetViewerItem;
  /** The asset label and the song title: the choice of a watermarked file names them (trial-upsell a2). */
  label: string;
  songTitle: string;
  isDemo: boolean;
  /** "Post it" shows only where the card of the surface shows it. */
  canPost: boolean;
  onPost: (item: AssetViewerItem) => void;
  /** "New revision" (Releases v2, design d1): the dialog d3 for the revision on the stage. */
  onNewRevision: () => void;
  /** False until the revision history loaded. */
  canMakeRevision: boolean;
  /** "New revision", "Edit the video" (a music video) or nothing (an added version). */
  revisionAction: ViewerRevisionAction;
  /** "Edit the video": the music video editor with the cut of the file. */
  onEditVideo: () => void;
  /** Below 700 px the share button sits in the grid (design a6); null on the demo artist. */
  narrowShare: AssetShareModel | null;
  onOpenShareSheet: () => void;
}

/**
 * The actions (designs a1, a5, a6; Releases v2 d1): Download over the full width, then "Post it" and
 * "New revision" ("Edit the video" for a music video; nothing for an added version, `viewerRevisionAction`). The demo artist keeps Download; "Post it" and "New revision" are off. Below 700 px the grid is
 * 2 × 2 with the share button: "Share link" makes and copies the link, "Link on" opens the box.
 * On Free and the trial, Download of a watermarked file opens the choice first (trial-upsell a2);
 * "Post it" and the share link do the same in their own hooks.
 */
const ViewerActionsComponent: React.FC<ViewerActionsProps> = ({
  item,
  label,
  songTitle,
  isDemo,
  canPost,
  onPost,
  onNewRevision,
  canMakeRevision,
  revisionAction,
  onEditVideo,
  narrowShare,
  onOpenShareSheet,
}) => {
  const intl = useIntl();
  const post = useCallback(() => onPost(item), [item, onPost]);
  const isLinkOn = narrowShare?.status === 'on';
  const share = isLinkOn ? onOpenShareSheet : narrowShare?.create;
  const choiceFile = useMemo(() => choiceFileOfViewerItem(item, label, songTitle), [item, label, songTitle]);

  return (
    <div className={styles.acts}>
      <WatermarkDownloadLink
        className={clsx(styles.btn, styles.btnPrimary, styles.full)}
        href={downloadMediaUrl(item.fileUrl)}
        hasWatermark={item.watermark}
        file={choiceFile}
        testId="viewer-download"
        onDownload={() => trackAssetDownloaded(item.themeId, 'viewer')}
      >
        <FontAwesomeIcon icon={faDownload} />
        {intl.formatMessage(viewerMessages.download)}
      </WatermarkDownloadLink>
      {narrowShare !== null && (
        <button
          type="button"
          className={styles.btn}
          disabled={narrowShare.status === 'loading' || narrowShare.isCreating}
          onClick={share}
        >
          <FontAwesomeIcon icon={faLink} />
          {intl.formatMessage(isLinkOn ? viewerMessages.linkOn : viewerMessages.shareLink)}
        </button>
      )}
      {(canPost || isDemo) && (
        <button type="button" className={styles.btn} disabled={isDemo} onClick={post} data-testid="viewer-post">
          <FontAwesomeIcon icon={faPaperPlane} />
          {intl.formatMessage(libraryMessages.postIt)}
        </button>
      )}
      {revisionAction.kind === 'newRevision' && (
        <button
          type="button"
          className={styles.btn}
          disabled={isDemo || !canMakeRevision}
          onClick={onNewRevision}
          data-testid="viewer-new-revision"
        >
          <FontAwesomeIcon icon={faArrowsRotate} />
          {intl.formatMessage(revisionMessages.newRevision)}
        </button>
      )}
      {revisionAction.kind === 'editVideo' && (
        <button
          type="button"
          className={styles.btn}
          disabled={isDemo}
          onClick={onEditVideo}
          data-testid="viewer-edit-video"
        >
          <FontAwesomeIcon icon={faPenToSquare} />
          {intl.formatMessage(addedAssetsMessages.editVideo)}
        </button>
      )}
    </div>
  );
};

export const ViewerActions = TypedMemo(ViewerActionsComponent);
